import { useState, useEffect } from "react";
import { Save, Loader2, BarChart2, Trophy, Shield, Users } from "lucide-react";
import { Button } from "../../ui/button";

export const LeagueStandingsSection = ({ league, onSave, saving }) => {
  const [standings, setStandings] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [viewMode, setViewMode] = useState("conference");

  const divisions = league.divisions || [];
  const conferences = league.conferences || [];

  useEffect(() => {
    const standingsWithTeams = (league.standings || []).map(s => ({
      ...s,
      team: league.teams?.find(t => t.id === s.team_id)
    }));
    standingsWithTeams.sort((a, b) => (b.wins || 0) - (a.wins || 0));
    setStandings(standingsWithTeams);
  }, [league]);

  const updateStanding = (standingId, field, value) => {
    setStandings(prev => prev.map(s => {
      const id = s.division_entry_id || s.team_id;
      return id === standingId ? { ...s, [field]: parseInt(value) || 0 } : s;
    }));
    setHasChanges(true);
  };

  const updateStreakField = (standingId, value) => {
    setStandings(prev => prev.map(st => {
      const id = st.division_entry_id || st.team_id;
      return id === standingId ? { ...st, streak: value } : st;
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    const toSave = standings.map(s => ({
      team_id: s.team_id,
      division_entry_id: s.division_entry_id || null,
      wins: s.wins || 0,
      losses: s.losses || 0,
      ties: s.ties || 0,
      conference_wins: s.conference_wins || 0,
      conference_losses: s.conference_losses || 0,
      division_wins: s.division_wins || 0,
      division_losses: s.division_losses || 0,
      points_for: s.points_for || 0,
      points_against: s.points_against || 0,
      streak: s.streak || "",
      notes: s.notes || ""
    }));
    onSave(toSave);
    setHasChanges(false);
  };

  const getTeamsForDivision = (divisionId) => {
    return standings.filter(s => {
      if (s.division_entry?.division_id === divisionId) return true;
      if (s.division?.id === divisionId) return true;
      return false;
    }).sort((a, b) => (b.wins || 0) - (a.wins || 0));
  };

  const getTeamsForConference = (conferenceId) => {
    return standings.filter(s => {
      const team = s.team;
      if (!team) return false;
      return team.conference_id === conferenceId;
    }).sort((a, b) => (b.wins || 0) - (a.wins || 0));
  };

  const getDivisionsForConference = (confId) => {
    const divisionIds = new Set();
    standings.forEach(s => {
      if (s.team?.conference_id === confId) {
        const divId = s.division_entry?.division_id || s.division?.id;
        if (divId) divisionIds.add(divId);
      }
    });
    return divisions.filter(d => divisionIds.has(d.id));
  };

  const getTeamsForConferenceAndDivision = (confId, divId) => {
    return standings.filter(s => {
      const team = s.team;
      if (!team || team.conference_id !== confId) return false;
      const standingDivId = s.division_entry?.division_id || s.division?.id;
      return standingDivId === divId;
    }).sort((a, b) => (b.wins || 0) - (a.wins || 0));
  };

  const getTeamsWithoutDivisionInConference = (confId) => {
    return standings.filter(s => {
      const team = s.team;
      if (!team || team.conference_id !== confId) return false;
      const hasDivision = s.division_entry?.division_id || s.division?.id;
      return !hasDivision;
    }).sort((a, b) => (b.wins || 0) - (a.wins || 0));
  };

  const getUnassignedTeams = () => {
    return standings.filter(s => {
      const team = s.team;
      if (!team) return true;
      return !team.conference_id;
    }).sort((a, b) => (b.wins || 0) - (a.wins || 0));
  };

  const getTeamsWithoutDivision = () => {
    return standings.filter(s => {
      const hasDivision = s.division_entry?.division_id || s.division?.id;
      return !hasDivision;
    }).sort((a, b) => (b.wins || 0) - (a.wins || 0));
  };

  const renderStandingsTable = (teamStandings, showRank = true) => (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-zinc-500 border-b border-zinc-800">
          {showRank && <th className="pb-3 font-medium w-8">#</th>}
          <th className="pb-3 font-medium">Team</th>
          <th className="pb-3 font-medium text-center">W</th>
          <th className="pb-3 font-medium text-center">L</th>
          <th className="pb-3 font-medium text-center">Conf W</th>
          <th className="pb-3 font-medium text-center">Conf L</th>
          <th className="pb-3 font-medium text-center">Div W</th>
          <th className="pb-3 font-medium text-center">Div L</th>
          <th className="pb-3 font-medium text-center">Streak</th>
        </tr>
      </thead>
      <tbody>
        {teamStandings.length === 0 ? (
          <tr>
            <td colSpan={showRank ? 9 : 8} className="py-4 text-center text-zinc-500">
              No teams
            </td>
          </tr>
        ) : (
          teamStandings.map((s, idx) => {
            const uniqueKey = s.division_entry_id || s.team_id;
            const standingId = s.division_entry_id || s.team_id;
            const displayName = s.display_name || s.division_entry?.display_name || s.team?.name || "Unknown";
            
            return (
              <tr key={uniqueKey} className="border-b border-zinc-800/50">
                {showRank && <td className="py-3 text-zinc-500">{idx + 1}</td>}
                <td className="py-3">
                  <div className="flex items-center gap-3">
                    {s.team?.logo_url ? (
                      <img src={s.team.logo_url} alt="" className="w-8 h-8 object-contain" />
                    ) : (
                      <div
                        className="w-8 h-8 rounded flex items-center justify-center"
                        style={{ backgroundColor: s.team?.color || "#333" }}
                      >
                        <span className="text-xs font-bold text-white">
                          {s.team?.abbreviation || s.team?.name?.substring(0, 2)}
                        </span>
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-white font-medium">{displayName}</span>
                      {s.division_entry_id && s.division?.name && (
                        <span className="text-xs text-zinc-500">{s.division.name}</span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="py-3 text-center">
                  <input
                    type="number"
                    value={s.wins || 0}
                    onChange={(e) => updateStanding(standingId, "wins", e.target.value)}
                    className="w-12 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-center text-white"
                    min="0"
                  />
                </td>
                <td className="py-3 text-center">
                  <input
                    type="number"
                    value={s.losses || 0}
                    onChange={(e) => updateStanding(standingId, "losses", e.target.value)}
                    className="w-12 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-center text-white"
                    min="0"
                  />
                </td>
                <td className="py-3 text-center">
                  <input
                    type="number"
                    value={s.conference_wins || 0}
                    onChange={(e) => updateStanding(standingId, "conference_wins", e.target.value)}
                    className="w-12 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-center text-white"
                    min="0"
                  />
                </td>
                <td className="py-3 text-center">
                  <input
                    type="number"
                    value={s.conference_losses || 0}
                    onChange={(e) => updateStanding(standingId, "conference_losses", e.target.value)}
                    className="w-12 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-center text-white"
                    min="0"
                  />
                </td>
                <td className="py-3 text-center">
                  <input
                    type="number"
                    value={s.division_wins || 0}
                    onChange={(e) => updateStanding(standingId, "division_wins", e.target.value)}
                    className="w-12 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-center text-white"
                    min="0"
                  />
                </td>
                <td className="py-3 text-center">
                  <input
                    type="number"
                    value={s.division_losses || 0}
                    onChange={(e) => updateStanding(standingId, "division_losses", e.target.value)}
                    className="w-12 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-center text-white"
                    min="0"
                  />
                </td>
                <td className="py-3 text-center">
                  <input
                    type="text"
                    value={s.streak || ""}
                    onChange={(e) => updateStreakField(standingId, e.target.value)}
                    placeholder="W3"
                    className="w-14 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-center text-white"
                  />
                </td>
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );

  if (standings.length === 0) {
    return (
      <div className="text-center py-12 text-zinc-500">
        <BarChart2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No teams in this league yet</p>
        <p className="text-sm">Add teams first to track standings</p>
      </div>
    );
  }

  const hasConferences = conferences.length > 0;
  const hasDivisions = divisions.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h4 className="text-sm font-bold text-zinc-400 uppercase">Team Standings</h4>
          {(hasConferences || hasDivisions) && (
            <div className="flex bg-zinc-800 rounded p-0.5">
              {hasConferences && (
                <button
                  type="button"
                  onClick={() => setViewMode("conference")}
                  className={`px-3 py-1 text-xs rounded ${viewMode === "conference" ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`}
                >
                  By Conference
                </button>
              )}
              {hasDivisions && (
                <button
                  type="button"
                  onClick={() => setViewMode("division")}
                  className={`px-3 py-1 text-xs rounded ${viewMode === "division" ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`}
                >
                  By Division
                </button>
              )}
              <button
                type="button"
                onClick={() => setViewMode("all")}
                className={`px-3 py-1 text-xs rounded ${viewMode === "all" ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-white"}`}
              >
                All Teams
              </button>
            </div>
          )}
        </div>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {/* Conference View */}
      {viewMode === "conference" && hasConferences && (
        <div className="space-y-6">
          {conferences.map(conf => {
            const confDivisions = getDivisionsForConference(conf.id);
            const confTeamsWithoutDiv = getTeamsWithoutDivisionInConference(conf.id);
            const allConfTeams = getTeamsForConference(conf.id);
            
            if (allConfTeams.length === 0) return null;
            
            return (
              <div key={conf.id} className="space-y-3">
                <div 
                  className="px-4 py-3 rounded-lg flex items-center gap-3"
                  style={{ backgroundColor: `${conf.color || league.color || '#f97316'}20` }}
                >
                  {conf.logo_url ? (
                    <img src={conf.logo_url} alt={conf.name} className="w-10 h-10 object-contain" />
                  ) : (
                    <Trophy className="w-5 h-5" style={{ color: conf.color || league.color || '#f97316' }} />
                  )}
                  <h5 className="text-white font-medium">
                    {conf.name}
                    <span className="text-xs text-zinc-500 ml-2">({allConfTeams.length} entries)</span>
                  </h5>
                </div>
                
                {confDivisions.length > 0 ? (
                  <div className="space-y-3 ml-4">
                    {confDivisions.map(div => {
                      const divTeams = getTeamsForConferenceAndDivision(conf.id, div.id);
                      if (divTeams.length === 0) return null;
                      
                      return (
                        <div key={div.id} className="bg-zinc-800/30 rounded-lg border border-zinc-700 overflow-hidden">
                          <div 
                            className="px-4 py-2 border-b border-zinc-700 flex items-center gap-2"
                            style={{ backgroundColor: `${div.color || conf.color || league.color || '#f97316'}10` }}
                          >
                            <div 
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: div.color || conf.color || league.color || '#f97316' }}
                            />
                            <Shield className="w-3 h-3 text-zinc-500" />
                            <span className="text-sm text-white font-medium">{div.name}</span>
                            <span className="text-xs text-zinc-500">({divTeams.length})</span>
                          </div>
                          <div className="overflow-x-auto p-2">
                            {renderStandingsTable(divTeams)}
                          </div>
                        </div>
                      );
                    })}
                    
                    {confTeamsWithoutDiv.length > 0 && (
                      <div className="bg-zinc-800/30 rounded-lg border border-zinc-700 overflow-hidden">
                        <div className="px-4 py-2 border-b border-zinc-700 bg-zinc-800/50 flex items-center gap-2">
                          <Users className="w-3 h-3 text-zinc-500" />
                          <span className="text-sm text-white font-medium">No Division</span>
                          <span className="text-xs text-zinc-500">({confTeamsWithoutDiv.length})</span>
                        </div>
                        <div className="overflow-x-auto p-2">
                          {renderStandingsTable(confTeamsWithoutDiv)}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="ml-4 bg-zinc-800/30 rounded-lg border border-zinc-700 overflow-hidden">
                    <div className="overflow-x-auto p-2">
                      {renderStandingsTable(allConfTeams)}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          
          {getUnassignedTeams().length > 0 && (
            <div className="bg-zinc-800/30 rounded-lg border border-zinc-700 overflow-hidden">
              <div className="px-4 py-2 border-b border-zinc-700 bg-zinc-800/50">
                <h5 className="text-white font-medium flex items-center gap-2">
                  <Users className="w-4 h-4 text-zinc-500" />
                  No Conference Assigned
                  <span className="text-xs text-zinc-500">({getUnassignedTeams().length})</span>
                </h5>
              </div>
              <div className="overflow-x-auto p-2">
                {renderStandingsTable(getUnassignedTeams())}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Division View */}
      {viewMode === "division" && hasDivisions && (
        <div className="space-y-4">
          {divisions.map(division => {
            const divisionTeams = getTeamsForDivision(division.id);
            
            return (
              <div key={division.id} className="bg-zinc-800/30 rounded-lg border border-zinc-700 overflow-hidden">
                <div 
                  className="px-4 py-2 border-b border-zinc-700"
                  style={{ backgroundColor: `${division.color || league.color || '#f97316'}15` }}
                >
                  <h5 className="text-white font-medium flex items-center gap-2">
                    <Shield className="w-4 h-4" style={{ color: division.color || league.color || '#f97316' }} />
                    {division.name}
                    <span className="text-xs text-zinc-500">({divisionTeams.length} teams)</span>
                  </h5>
                </div>
                <div className="overflow-x-auto p-2">
                  {renderStandingsTable(divisionTeams)}
                </div>
              </div>
            );
          })}
          
          {getTeamsWithoutDivision().length > 0 && (
            <div className="bg-zinc-800/30 rounded-lg border border-zinc-700 overflow-hidden">
              <div className="px-4 py-2 border-b border-zinc-700 bg-zinc-800/50">
                <h5 className="text-white font-medium flex items-center gap-2">
                  <Users className="w-4 h-4 text-zinc-500" />
                  No Division Assigned
                  <span className="text-xs text-zinc-500">({getTeamsWithoutDivision().length})</span>
                </h5>
              </div>
              <div className="overflow-x-auto p-2">
                {renderStandingsTable(getTeamsWithoutDivision())}
              </div>
            </div>
          )}
        </div>
      )}

      {/* All Teams View */}
      {viewMode === "all" && (
        <div className="overflow-x-auto">
          {renderStandingsTable(standings)}
        </div>
      )}
    </div>
  );
};
